import React, { useState, useRef, memo, useCallback } from 'react';

/**
 * EditableCell - Text editing cell with auto-save and optional drag-to-fill
 * 
 * Props:
 * - value: Current cell value
 * - rowIndex: Index of the row in the data array
 * - rowId: Unique ID of the row (for saving)
 * - columnId: ID of the column
 * - hasEditPermission: Whether the user can edit this cell
 * - onUpdate: Callback to update local data (rowIndex, columnId, value)
 * - onSave: Callback to save to backend (rowId, columnId, oldValue, newValue) -> Promise
 * - isDragging: Whether drag-fill is currently active
 * - onDragFillStart: Callback to start drag-fill (e, rowIndex, columnId, value)
 * - showDragHandle: Whether to show the drag-fill handle (default: true)
 * - isRetrying: Whether the save is being retried due to lock conflict
 */
const EditableCell = memo(({ 
  value, 
  rowIndex,
  rowId,
  columnId,
  hasEditPermission = false,
  onUpdate,
  onSave,
  isDragging = false,
  onDragFillStart,
  showDragHandle = true,
  isRetrying = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const cellRef = useRef(null);
  const inputRef = useRef(null);

  const handleClick = useCallback(() => {
    if (!isDragging && hasEditPermission && !isEditing) {
      setIsEditing(true);
      setEditValue(value || '');
      // Focus input after state update, with preventScroll to avoid layout shift
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus({ preventScroll: true });
        }
      }, 0);
    }
  }, [isDragging, hasEditPermission, isEditing, value]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    const normalizedValue = value || '';
    const normalizedEditValue = editValue || '';
    
    if (normalizedEditValue !== normalizedValue) {
      // Update local data immediately (using rowId for correct lookup in filtered/sorted data)
      if (onUpdate) {
        onUpdate(rowId, columnId, editValue);
      }
      
      // Fire-and-forget: save in background without blocking UI
      if (onSave) {
        setIsSaving(true);
        onSave(rowId, columnId, value, editValue)
          .then(() => {
            setIsSaving(false);
          })
          .catch(error => {
            setIsSaving(false);
            console.error('Background save failed:', error);
          });
      }
    }
  }, [value, editValue, rowId, columnId, onUpdate, onSave]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  }, [handleBlur, value]);

  const handleDragStart = useCallback((e) => {
    if (onDragFillStart) {
      onDragFillStart(e, rowIndex, columnId, value || '');
    }
  }, [onDragFillStart, rowIndex, columnId, value]);

  const displayValue = value || '';

  return (
    <div
      ref={cellRef}
      onClick={handleClick}
      style={{
        cursor: hasEditPermission ? 'pointer' : 'default',
        fontSize: '11px',
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
      title={hasEditPermission ? "Click to edit" : "View only - no edit permission"}
    >
      {/* Display text - centered via absolute positioning */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          pointerEvents: isDragging ? 'none' : 'auto',
          color: !displayValue ? '#999' : 'inherit',
          opacity: isEditing ? 0 : 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 4px',
        }}
      >
        {displayValue || '(empty)'}
      </div>

      {/* Edit input - absolute overlay */}
      {isEditing && (
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            padding: '2px 4px',
            border: '2px solid #007bff',
            borderRadius: '2px',
            fontSize: '11px',
            textAlign: 'center',
            boxSizing: 'border-box',
            outline: 'none',
            zIndex: 5,
            backgroundColor: 'white',
          }}
        />
      )}

      {/* Saving indicator - absolute positioned */}
      {(isSaving || isRetrying) && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            right: '4px',
            transform: 'translateY(-50%)',
            width: '10px',
            height: '10px',
            border: `2px solid ${isRetrying ? '#ffe4c4' : '#f3f3f3'}`,
            borderTop: `2px solid ${isRetrying ? '#ff8c00' : '#007bff'}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            zIndex: 6,
          }}
          title={isRetrying ? 'Retrying save...' : 'Saving...'}
        />
      )}

      {/* Drag fill handle - always absolute positioned */}
      {showDragHandle && hasEditPermission && onDragFillStart && (
        <div
          onMouseDown={handleDragStart}
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '7px',
            height: '7px',
            backgroundColor: '#007bff',
            cursor: 'crosshair',
            borderRadius: '1px',
            zIndex: 10,
            boxShadow: '0 0 1px rgba(0,0,0,0.3)',
          }}
          title="Drag to fill cells"
        />
      )}
    </div>
  );
});

EditableCell.displayName = 'EditableCell';

export default EditableCell;

